import React, { useState } from 'react';
import { useObservable } from 'react-use-observable';

import { Modal, Button, Spin } from 'antd';

import Alert from 'components/Alert';
import { useFormik } from 'formik';
import type { FormikHelpers } from 'formik';
import type { Observable } from 'rxjs';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import * as Yup from 'yup';

import { Content } from './Content';

interface IValues {
  email: string;
}

interface IProps {
  onClose: () => void;
  open: boolean;
  onOk: (email: string, callback: () => void) => Observable<any>;
  message?: string;
  reportDescription: string;
  shouldInformUnregisteredEmail?: boolean;
}

export const BackgroundExportModal = ({
  open,
  onClose,
  onOk,
  reportDescription,
  shouldInformUnregisteredEmail,
  message
}: IProps) => {
  const [success, setSuccess] = useState(false);
  const [sentEmail, setSentEmail] = useState(null);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const onSubmit = async (values: IValues, form: FormikHelpers<IValues>) => {
    let confirmation = true;

    if (shouldInformUnregisteredEmail && values.email && user?.email !== values.email) {
      confirmation = await Alert.confirm({
        message: 'Esse e-mail é diferente do e-mail de cadastro, tem certeza que deseja enviar para esse e-mail?',
        title: 'Atenção',
        buttonOk: 'Confirmar',
        confirmation: true,
        size: 'xs'
      });
    }

    if (!confirmation) return;
    const observable = onOk(values.email, () => {
      setSentEmail(values.email);
      setSuccess(true);
      form.setSubmitting(false);
      form.resetForm();
    });
    observable.subscribe();
  };

  const formik = useFormik<IValues>({
    onSubmit,
    validationSchema: Yup.object().shape({
      email: Yup.string().email()
    }),
    initialValues: {
      email: ''
    }
  });

  const handleClose = () => {
    onClose();
    setSuccess(false);
    setSentEmail(null);
    formik.resetForm();
  };

  return (
    <Modal
      width={600}
      onCancel={handleClose}
      open={open}
      closable
      title={`Exportar relatório de ${reportDescription}`}
      footer={
        <div className='flex justify-end gap-2'>
          <Button id='background-export-close-button' onClick={handleClose}>
            Fechar
          </Button>
          {!success && (
            <Button
              id='background-export-generate-button'
              type='primary'
              disabled={!formik.isValid || formik.isSubmitting}
              icon={formik.isSubmitting ? <Spin size='small' /> : undefined}
              onClick={() => formik.handleSubmit()}
            >
              Gerar
            </Button>
          )}
        </div>
      }
    >
      <Content success={success} sentEmail={sentEmail} message={message} />
    </Modal>
  );
};
